import { useState, useEffect, useCallback, useRef } from 'react';
import { isLoggedIn, clearToken, apiFetch } from './api';
import useCalendarEvents from './useCalendarEvents';
import { loadLayoutsFromServer, saveLayoutsToServer, isMobileDevice, PANEL_NAMES, PANEL_GROUPS } from './layoutManager';
import Login from './components/Login';
import LockScreen from './components/LockScreen';
import Clock from './components/Clock';
import AnalogClock from './components/AnalogClock';
import CalendarSection from './components/CalendarSection';
import CalendarMonth from './components/CalendarMonth';
import WeatherWidget from './components/WeatherWidget';
import HourlyWeather from './components/HourlyWeather';
import GoalsSection from './components/GoalsSection';
import GoalsManager from './components/GoalsManager';
import GoalsChart from './components/GoalsChart';
import GoalsHistory from './components/GoalsHistory';
import AirbnbReservations from './components/AirbnbReservations';
import AirbnbCalendar from './components/AirbnbCalendar';
import AirbnbEarnings from './components/AirbnbEarnings';
import AirbnbAlerts from './components/AirbnbAlerts';
import AirbnbYTD from './components/AirbnbYTD';
import ShoppingList from './components/ShoppingList';
import CountdownTimers from './components/CountdownTimers';
import StockTickers from './components/StockTickers';
import NewsTicker from './components/NewsTicker';
import TrafficWidget from './components/TrafficWidget';
import QuoteOfDay from './components/QuoteOfDay';
import ReceiptsPanel from './components/ReceiptsPanel';
import EmailOverviewPanel from './components/EmailOverviewPanel';
import BackgroundProcessesPanel from './components/BackgroundProcessesPanel';
import ReceiptRecordsPanel from './components/ReceiptRecordsPanel';
import EmailBrowser from './components/EmailBrowser';
import ReceiptExporter from './components/ReceiptExporter';
import PackageTracking from './components/PackageTracking';
import WeddingGuestTracker from './components/WeddingGuestTracker';
import WeddingDashboard from './components/WeddingDashboard';
import WebEmbed from './components/WebEmbed';
import SimpleFinSync from './components/SimpleFinSync';
import AccountBalances from './components/AccountBalances';
import AccountSnapshotsPanel from './components/AccountSnapshotsPanel';
import TransactionsPanel from './components/TransactionsPanel';
import EventModal from './components/EventModal';
import NotificationsPanel from './components/NotificationsPanel';
import NetWorthChart from './components/NetWorthChart';
import FinancialDashboard from './components/FinancialDashboard';
import TransactionReports from './components/TransactionReports';
import CategoryRulesPanel from './components/CategoryRulesPanel';
import CategoryConfigPanel from './components/CategoryConfigPanel';
import GlobalSearch from './components/GlobalSearch';
import DataExport from './components/DataExport';
import PortfolioPanel from './components/PortfolioPanel';
import KpiSummary from './components/KpiSummary';
import LayoutNavigator from './components/LayoutNavigator';
import CulversFunding from './components/CulversFunding';
import CulversFutureFunding from './components/CulversFutureFunding';
import MonthlyBudgetPanel from './components/MonthlyBudgetPanel';
import StatsPanel from './components/StatsPanel';
import DashboardPanels from './components/DashboardPanels';
import SettingsPage from './components/SettingsPage';
import BackupAdmin from './components/BackupAdmin';
import './App.css';

function App() {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());
  const [view, setView] = useState('dashboard');
  const [goalsData, setGoalsData] = useState({ goals: [], completions: [] });
  const [bookingData, setBookingData] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [locked, setLocked] = useState(() => sessionStorage.getItem('meware_locked') === 'true');
  const [nightMode, setNightMode] = useState(false);
  const [nightModeManual, setNightModeManual] = useState(false);
  const [nightModeSchedule, setNightModeSchedule] = useState(null);
  const [receiptRefreshKey, setReceiptRefreshKey] = useState(0);
  const [simpleFinRefreshKey, setSimpleFinRefreshKey] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [buildVersion, setBuildVersion] = useState('');
  const [showVersionBanner, setShowVersionBanner] = useState(false);
  const buildVersionRef = useRef('');

  // Layout state
  const [layouts, setLayouts] = useState([]);
  const [layoutIndex, setLayoutIndex] = useState(0);
  const [layoutsLoaded, setLayoutsLoaded] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const [flyoutGroup, setFlyoutGroup] = useState(null);
  const [newLayoutName, setNewLayoutName] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [renamingIndex, setRenamingIndex] = useState(null);
  const [renameText, setRenameText] = useState('');
  const [navHistory, setNavHistory] = useState([]);
  const saveTimer = useRef(null);

  const [layoutConflict, setLayoutConflict] = useState(false);

  const currentLayout = layouts[layoutIndex] || { order: [], hidden: [], columns: 2, name: 'Default' };
  const isIphone = /iPhone/i.test(navigator.userAgent);

  const { events: calendarEvents, loading: calLoading, error: calError } = useCalendarEvents(refreshKey);

  const layoutVersionRef = useRef(0);
  const layoutLastUpdatedRef = useRef(null);
  const isMobile = isMobileDevice();

  // Load layouts from server
  useEffect(() => {
    if (!loggedIn) return;
    loadLayoutsFromServer().then(({ layouts: l, currentIndex, version, lastUpdated }) => {
      setLayouts(l);
      layoutVersionRef.current = version || 0;
      layoutLastUpdatedRef.current = lastUpdated || null;
      // Prefer this browser's last-used layout over server default
      const localIdx = parseInt(localStorage.getItem('meware_local_layout'));
      const idx = !isNaN(localIdx) && localIdx < l.length ? localIdx : (currentIndex < l.length ? currentIndex : 0);
      setLayoutIndex(idx);
      setLayoutsLoaded(true);
    });
  }, [loggedIn]);

  // Debounced save to server
  const saveToServer = useCallback((newLayouts, newIndex) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const result = await saveLayoutsToServer(newLayouts, newIndex, layoutVersionRef.current, layoutLastUpdatedRef.current);
      if (result?.conflict) {
        // Server has newer version — show conflict notification and reload
        setLayoutConflict(true);
        const fresh = await loadLayoutsFromServer();
        setLayouts(fresh.layouts);
        layoutVersionRef.current = fresh.version || 0;
        layoutLastUpdatedRef.current = fresh.lastUpdated || null;
        if (newIndex >= fresh.layouts.length) setLayoutIndex(0);
      } else if (result?.version) {
        layoutVersionRef.current = result.version;
        if (result.lastUpdated) layoutLastUpdatedRef.current = result.lastUpdated;
      }
    }, 500);
  }, []);

  const fetchGoals = useCallback(async () => {
    try {
      const res = await apiFetch('/api/goals');
      const data = await res.json();
      setGoalsData(data);
    } catch (err) {
      console.error('Failed to fetch goals:', err);
    }
  }, []);

  const fetchBookingData = useCallback(async () => {
    try {
      const res = await apiFetch('/api/bookings');
      const data = await res.json();
      setBookingData(data);
    } catch (err) {
      console.error('Failed to fetch booking data:', err);
    }
  }, []);

  useEffect(() => {
    if (loggedIn) {
      fetchGoals();
      fetchBookingData();
    }
  }, [loggedIn, fetchGoals, fetchBookingData, refreshKey]);

  useEffect(() => {
    if (!loggedIn) return;
    let interval = setInterval(() => {
      setRefreshKey((k) => k + 1);
      setLastRefreshed(new Date());
    }, 10 * 60 * 1000);

    // Pause refresh when tab is hidden to save resources on slow machines
    function handleVisibility() {
      if (document.hidden) {
        clearInterval(interval);
        interval = null;
      } else if (!interval) {
        // Tab became visible — refresh now and restart timer
        setRefreshKey((k) => k + 1);
        setLastRefreshed(new Date());
        interval = setInterval(() => {
          setRefreshKey((k) => k + 1);
          setLastRefreshed(new Date());
        }, 10 * 60 * 1000);
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loggedIn]);

  // Version check — fetch on load and every 10 min refresh
  useEffect(() => {
    if (!loggedIn) return;
    async function checkVersion() {
      try {
        const res = await fetch('/api/public/version');
        if (!res.ok) return;
        const data = await res.json();
        const serverVersion = data.version;
        if (!buildVersionRef.current) {
          // First load — just store it
          buildVersionRef.current = serverVersion;
          setBuildVersion(serverVersion);
        } else if (serverVersion !== buildVersionRef.current) {
          // New version detected
          setBuildVersion(serverVersion);
          setShowVersionBanner(true);
          setTimeout(() => {
            window.location.reload();
          }, 10000);
        }
      } catch (err) {
        // Ignore fetch errors
      }
    }
    checkVersion();
  }, [loggedIn, refreshKey]);

  // Load night mode schedule from server
  useEffect(() => {
    if (!loggedIn) return;
    apiFetch('/api/settings/night-mode-schedule')
      .then((res) => res.json())
      .then((data) => {
        setNightModeSchedule(data);
        // Apply night mode immediately on load if within schedule window
        if (data && data.enabled) {
          const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
          const currentMinutes = now.getHours() * 60 + now.getMinutes();
          const startMinutes = data.startHour * 60 + data.startMinute;
          const endMinutes = data.endHour * 60 + data.endMinute;
          let isNight;
          if (startMinutes > endMinutes) {
            isNight = currentMinutes >= startMinutes || currentMinutes < endMinutes;
          } else {
            isNight = currentMinutes >= startMinutes && currentMinutes < endMinutes;
          }
          if (isNight && !isIphone) setNightMode(true);
        }
      })
      .catch(() => {});
  }, [loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto night mode on default layout
  useEffect(() => {
    if (!nightModeSchedule || !nightModeSchedule.enabled || nightModeManual) return;
    if (layoutIndex !== 0) return; // Only on default layout
    if (isIphone) return; // Never use night mode on iPhone

    function checkNightMode() {
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const startMinutes = nightModeSchedule.startHour * 60 + nightModeSchedule.startMinute;
      const endMinutes = nightModeSchedule.endHour * 60 + nightModeSchedule.endMinute;

      let isNight;
      if (startMinutes > endMinutes) {
        // Crosses midnight (e.g., 23:00 - 6:30)
        isNight = currentMinutes >= startMinutes || currentMinutes < endMinutes;
      } else {
        isNight = currentMinutes >= startMinutes && currentMinutes < endMinutes;
      }
      setNightMode((prev) => {
        if (prev !== isNight) {
          apiFetch('/api/activity-log', { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ category: 'UI', action: 'Night Mode Auto', detail: isNight ? 'Auto-switched to dark mode' : 'Auto-switched to light mode' }) }).catch(() => {});
        }
        return isNight;
      });
    }

    checkNightMode();
    const interval = setInterval(checkNightMode, 60 * 1000);
    return () => clearInterval(interval);
  }, [nightModeSchedule, nightModeManual, layoutIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRefresh() {
    setRefreshKey((k) => k + 1);
    setLastRefreshed(new Date());

    // Check if layouts changed on server (e.g. from another device)
    try {
      const { layouts: serverLayouts, version } = await loadLayoutsFromServer();
      layoutVersionRef.current = version || 0;
      if (JSON.stringify(serverLayouts) !== JSON.stringify(layouts)) {
        setLayouts(serverLayouts);
        if (layoutIndex >= serverLayouts.length) {
          setLayoutIndex(0);
        }
      }
    } catch (err) {
      console.error('Failed to check layout updates:', err);
    }
  }

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  function handleLogout() {
    clearToken();
    setLoggedIn(false);
  }

  function handleLayoutChange(changes) {
    if (isMobile && layoutIndex === 0) return; // Mobile cannot edit Default layout
    const updated = layouts.map((l, i) => (i === layoutIndex ? { ...l, ...changes } : l));
    setLayouts(updated);
    saveToServer(updated, layoutIndex);
  }

  function switchLayout(idx) {
    setLayoutIndex(idx);
    localStorage.setItem('meware_local_layout', idx);
    setShowLayoutMenu(false);
    setNavHistory([]);
    saveToServer(layouts, idx);
  }

  function navigateToLayout(idx) {
    setNavHistory((prev) => [...prev, layoutIndex]);
    setLayoutIndex(idx);
  }

  function navigateBack() {
    if (navHistory.length === 0) return;
    const prev = navHistory[navHistory.length - 1];
    setNavHistory((h) => h.slice(0, -1));
    setLayoutIndex(prev);
  }

  function handleAddLayout(e) {
    e.preventDefault();
    const name = newLayoutName.trim() || `Layout ${layouts.length + 1}`;
    const allPanels = [...new Set([...currentLayout.order])];
    const newLayout = { name, order: allPanels, hidden: [...allPanels], columns: 2 };
    const updated = [...layouts, newLayout];
    const newIdx = updated.length - 1;
    setLayouts(updated);
    setLayoutIndex(newIdx);
    localStorage.setItem('meware_local_layout', newIdx);
    setNewLayoutName('');
    setShowLayoutMenu(false);
    saveToServer(updated, newIdx);
  }

  function handleDeleteLayout(idx) {
    if (layouts.length <= 1 || idx === 0) return;
    const updated = layouts.filter((_, i) => i !== idx);
    const newIdx = idx >= updated.length ? updated.length - 1 : idx;
    setLayouts(updated);
    setLayoutIndex(newIdx);
    localStorage.setItem('meware_local_layout', newIdx);
    setShowLayoutMenu(false);
    saveToServer(updated, newIdx);
  }

  function handleMoveLayout(idx, direction) {
    const targetIdx = idx + direction;
    // Cannot move to/from index 0 (Default)
    if (idx === 0 || targetIdx === 0 || targetIdx < 0 || targetIdx >= layouts.length) return;
    const updated = [...layouts];
    [updated[idx], updated[targetIdx]] = [updated[targetIdx], updated[idx]];
    // Adjust current index if needed
    let newIdx = layoutIndex;
    if (layoutIndex === idx) newIdx = targetIdx;
    else if (layoutIndex === targetIdx) newIdx = idx;
    setLayouts(updated);
    setLayoutIndex(newIdx);
    localStorage.setItem('meware_local_layout', newIdx);
    saveToServer(updated, newIdx);
  }

  function startRename(idx) {
    if (idx === 0) return; // Cannot rename Default
    setRenamingIndex(idx);
    setRenameText(layouts[idx].name);
  }

  function handleRename(e) {
    e.preventDefault();
    if (renamingIndex === null || renamingIndex === 0) return;
    const name = renameText.trim();
    if (!name) return;
    const updated = layouts.map((l, i) => (i === renamingIndex ? { ...l, name } : l));
    setLayouts(updated);
    setRenamingIndex(null);
    setRenameText('');
    saveToServer(updated, layoutIndex);
  }

  function setColumns(n) {
    handleLayoutChange({ columns: n });
  }

  function restorePanel(panelId) {
    const newHidden = currentLayout.hidden.filter((id) => id !== panelId);
    handleLayoutChange({ hidden: newHidden });
    setShowAddMenu(false);
  }

  if (!loggedIn) {
    return <Login onLogin={() => setLoggedIn(true)} />;
  }

  if (locked) {
    return <LockScreen onUnlock={() => setLocked(false)} />;
  }

  if (!layoutsLoaded) {
    return <div className="app"><div className="loading" style={{ padding: 40, textAlign: 'center' }}>Loading dashboard...</div></div>;
  }

  if (view === 'settings') {
    return (
      <div className={`app${nightMode ? ' night-mode' : ''}`}>
        <header className="app-header">
          <div className="header-left">
            <h1>Settings</h1>
          </div>
          <div className="header-actions">
            <button className="back-btn" onClick={() => setView('dashboard')}>
              Back to Dashboard
            </button>
            <button className="logout-btn" onClick={() => setShowLogoutConfirm(true)}>
              Log Out
            </button>
          </div>
        </header>
        <SettingsPage
          nightModeSchedule={nightModeSchedule}
          onSaveSchedule={async (schedule) => {
            await apiFetch('/api/settings/night-mode-schedule', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(schedule),
            });
            setNightModeSchedule(schedule);
            setNightModeManual(false);
          }}
          onBack={() => setView('dashboard')}
          buildVersion={buildVersion}
        />
        {showLogoutConfirm && (
          <div className="modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
            <div className="modal-content logout-confirm" onClick={(e) => e.stopPropagation()}>
              <h2>Log Out?</h2>
              <p className="logout-confirm-text">Are you sure you want to log out?</p>
              <div className="logout-confirm-actions">
                <button className="logout-confirm-cancel" onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
                <button className="logout-confirm-yes" onClick={handleLogout}>Log Out</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (view === 'manage-goals') {
    return (
      <div className="app">
        <header className="app-header">
          <div className="header-actions">
            <button className="back-btn" onClick={() => setView('dashboard')}>
              Back to Dashboard
            </button>
            <button className="logout-btn" onClick={() => setShowLogoutConfirm(true)}>
              Log Out
            </button>
          </div>
        </header>
        <GoalsManager goalsData={goalsData} onUpdate={fetchGoals} />
      </div>
    );
  }

  const panelMap = {
    'clock': <Clock />,
    'analog-clock': <AnalogClock />,
    'upcoming-events': (
      <CalendarSection
        events={calendarEvents}
        loading={calLoading}
        error={calError}
        onEventClick={setSelectedEvent}
      />
    ),
    'calendar-month': (
      <CalendarMonth
        events={calendarEvents}
        loading={calLoading}
        onEventClick={setSelectedEvent}
        compact={(currentLayout.heights || {})['calendar-month'] === 2}
      />
    ),
    'weather': <WeatherWidget refreshKey={refreshKey} />,
    'hourly-weather': <HourlyWeather refreshKey={refreshKey} />,
    'traffic': <TrafficWidget refreshKey={refreshKey} />,
    'tickers': <StockTickers refreshKey={refreshKey} />,
    'news': <NewsTicker refreshKey={refreshKey} />,
    'shopping': <ShoppingList refreshKey={refreshKey} />,
    'countdowns': <CountdownTimers refreshKey={refreshKey} />,
    'airbnb-alerts': <AirbnbAlerts bookingData={bookingData} refreshKey={refreshKey} />,
    'airbnb': (
      <AirbnbReservations
        bookingData={bookingData}
        onDataChange={fetchBookingData}
        refreshKey={refreshKey}
      />
    ),
    'airbnb-calendar': (
      <AirbnbCalendar bookingData={bookingData} refreshKey={refreshKey} />
    ),
    'airbnb-earnings': (
      <AirbnbEarnings bookingData={bookingData} refreshKey={refreshKey} />
    ),
    'airbnb-ytd': <AirbnbYTD bookingData={bookingData} refreshKey={refreshKey} />,
    'goals': (
      <GoalsSection
        goalsData={goalsData}
        onUpdate={fetchGoals}
        onManage={() => setView('manage-goals')}
      />
    ),
    'goals-chart': <GoalsChart goalsData={goalsData} />,
    'goals-history': <GoalsHistory goalsData={goalsData} onUpdate={fetchGoals} />,
    'receipts': <ReceiptsPanel refreshKey={refreshKey} onReceiptCreated={() => setReceiptRefreshKey((k) => k + 1)} onImportComplete={() => setReceiptRefreshKey((k) => k + 1)} />,
    'receipt-emails': <ReceiptsPanel refreshKey={refreshKey} onReceiptCreated={() => setReceiptRefreshKey((k) => k + 1)} onImportComplete={() => setReceiptRefreshKey((k) => k + 1)} subjectFilter="Receipt" panelTitle="Email Receipt Importer" />,
    'email-browser': <EmailBrowser refreshKey={refreshKey} />,
    'receipt-records': <ReceiptRecordsPanel refreshKey={refreshKey} extraRefreshKey={receiptRefreshKey} />,
    'receipt-exporter': <ReceiptExporter />,
    'packages': <PackageTracking refreshKey={refreshKey} />,
    'wedding-guests': <WeddingGuestTracker refreshKey={refreshKey} />,
    'wedding-dashboard': <WeddingDashboard refreshKey={refreshKey} />,
    'web-embed': <WebEmbed refreshKey={refreshKey} panelId="web-embed" />,
    'simplefin': <SimpleFinSync refreshKey={refreshKey} onSyncComplete={() => setSimpleFinRefreshKey((k) => k + 1)} />,
    'account-balances': <AccountBalances refreshKey={refreshKey} extraRefreshKey={simpleFinRefreshKey} />,
    'account-snapshots': <AccountSnapshotsPanel refreshKey={refreshKey} />,
    'transactions': <TransactionsPanel refreshKey={refreshKey} extraRefreshKey={simpleFinRefreshKey} />,
    'net-worth-chart': <NetWorthChart refreshKey={refreshKey} />,
    'financial-dashboard': <FinancialDashboard refreshKey={refreshKey} extraRefreshKey={simpleFinRefreshKey} />,
    'transaction-reports': <TransactionReports refreshKey={refreshKey} extraRefreshKey={simpleFinRefreshKey} />,
    'category-rules': <CategoryRulesPanel refreshKey={refreshKey} onRulesApplied={() => setSimpleFinRefreshKey((k) => k + 1)} />,
    'category-config': <CategoryConfigPanel refreshKey={refreshKey} />,
    'global-search': <GlobalSearch refreshKey={refreshKey} />,
    'data-export': <DataExport />,
    'email-overview': <EmailOverviewPanel refreshKey={refreshKey} />,
    'background-processes': <BackgroundProcessesPanel refreshKey={refreshKey} />,
    'portfolio': <PortfolioPanel refreshKey={refreshKey} />,
    'kpi-summary': <KpiSummary refreshKey={refreshKey} extraRefreshKey={simpleFinRefreshKey} />,
    'layout-nav': <LayoutNavigator layouts={layouts} currentIndex={layoutIndex} onNavigate={navigateToLayout} />,
    'culvers-funding': <CulversFunding refreshKey={refreshKey} extraRefreshKey={simpleFinRefreshKey} />,
    'culvers-future': <CulversFutureFunding refreshKey={refreshKey} extraRefreshKey={simpleFinRefreshKey} />,
    'monthly-budget': <MonthlyBudgetPanel refreshKey={refreshKey} />,
    'stats': <StatsPanel refreshKey={refreshKey} bookingData={bookingData} />,
    'quote': <QuoteOfDay refreshKey={refreshKey} />,
    'notifications': <NotificationsPanel refreshKey={refreshKey} />,
    'backup-admin': <BackupAdmin />,
  };

  const hiddenPanels = currentLayout.order
    .filter((id) => currentLayout.hidden.includes(id) && panelMap[id])
    .map((id) => ({ id, name: PANEL_NAMES[id] || id }));

  return (
    <div className={`app${nightMode ? ' night-mode' : ''}`}>
      <header className="app-header">
        <div className="header-left">
          <button className="lock-btn" onClick={() => { setLocked(true); sessionStorage.setItem('meware_locked', 'true'); }} title="Lock">
            &#x1F512;
          </button>
          {!isIphone && <button
            className={`night-btn${nightMode ? ' active' : ''}`}
            onClick={() => {
              const newMode = !nightMode;
              setNightMode(newMode);
              setNightModeManual(true);
              apiFetch('/api/activity-log', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ category: 'UI', action: 'Night Mode Toggle', detail: newMode ? 'Switched to dark mode' : 'Switched to light mode' }) }).catch(() => {});
            }}
            title={nightMode ? 'Day mode' : 'Night mode'}
          >
            {nightMode ? '\u2600\uFE0F' : '\uD83C\uDF19'}
          </button>}
          <div className="header-date">
            {new Date().toLocaleDateString('en-US', {
              timeZone: 'America/Chicago',
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </div>
          <span className="last-refreshed">
            Updated {lastRefreshed.toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' })}
          </span>
        </div>
        <div className="header-title">Me<span style={{ color: '#3b82f6' }}>Ware</span></div>
        <div className="header-actions">
          <button
            className={`edit-mode-btn${editMode ? ' active' : ''}`}
            onClick={() => setEditMode(!editMode)}
            title={editMode ? 'Done editing' : 'Customize'}
          >
            {editMode ? 'Done' : '\u2699\uFE0F Customize'}
          </button>
          {layouts.length > 1 && (
            <div className="layout-container">
              <button
                className="layout-btn"
                onClick={() => { setShowLayoutMenu(!showLayoutMenu); setShowAddMenu(false); }}
              >
                {currentLayout.name}
                <span className="layout-arrow"> &#x25BE;</span>
              </button>
              {showLayoutMenu && (
                <div className="add-panel-menu layout-menu">
                  {layouts.map((l, i) => (
                    <div key={i} className="layout-option-row">
                      {renamingIndex === i ? (
                        <form onSubmit={handleRename} className="layout-rename-form">
                          <input
                            type="text"
                            value={renameText}
                            onChange={(e) => setRenameText(e.target.value)}
                            className="layout-rename-input"
                            autoFocus
                            onBlur={() => setRenamingIndex(null)}
                            onKeyDown={(e) => { if (e.key === 'Escape') setRenamingIndex(null); }}
                          />
                        </form>
                      ) : (
                        <button
                          className={`add-panel-option${i === layoutIndex ? ' active-layout' : ''}`}
                          onClick={() => switchLayout(i)}
                          onDoubleClick={() => startRename(i)}
                          title={i === 0 ? '' : 'Double-click to rename'}
                        >
                          {l.name}
                        </button>
                      )}
                      {i !== 0 && (
                        <div className="layout-reorder-btns">
                          <button
                            className="layout-reorder"
                            onClick={(e) => { e.stopPropagation(); handleMoveLayout(i, -1); }}
                            disabled={i <= 1}
                            title="Move up"
                          >
                            &#x25B4;
                          </button>
                          <button
                            className="layout-reorder"
                            onClick={(e) => { e.stopPropagation(); handleMoveLayout(i, 1); }}
                            disabled={i >= layouts.length - 1}
                            title="Move down"
                          >
                            &#x25BE;
                          </button>
                        </div>
                      )}
                      {i !== 0 && layouts.length > 1 && (
                        <button
                          className="layout-delete"
                          onClick={(e) => { e.stopPropagation(); handleDeleteLayout(i); }}
                          title="Delete layout"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}
                  <form onSubmit={handleAddLayout} className="layout-add-form">
                    <input
                      type="text"
                      value={newLayoutName}
                      onChange={(e) => setNewLayoutName(e.target.value)}
                      placeholder="New layout name..."
                      className="layout-add-input"
                    />
                    <button type="submit" className="layout-add-btn">+ Add</button>
                  </form>
                </div>
              )}
            </div>
          )}
          <button
            className="settings-btn"
            onClick={() => setView('settings')}
            title="Settings"
          >
            &#x2699;
          </button>
          <button className="refresh-btn" onClick={handleRefresh} title="Refresh">
            &#x21bb;
          </button>
          <button className="logout-btn" onClick={() => setShowLogoutConfirm(true)}>
            Log Out
          </button>
        </div>
        {editMode && (
          <div className="header-actions" style={{ marginTop: 4 }}>
            <div className="column-toggle">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <button
                  key={n}
                  className={`col-btn${currentLayout.columns === n ? ' active' : ''}`}
                  onClick={() => setColumns(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            {layouts.length <= 1 && (
              <div className="layout-container">
                <button
                  className="layout-btn"
                  onClick={() => { setShowLayoutMenu(!showLayoutMenu); setShowAddMenu(false); }}
                >
                  Add a Layout?
                </button>
                {showLayoutMenu && (
                  <div className="add-panel-menu layout-menu">
                    <form onSubmit={handleAddLayout} className="layout-add-form">
                      <input
                        type="text"
                        value={newLayoutName}
                        onChange={(e) => setNewLayoutName(e.target.value)}
                        placeholder="New layout name..."
                        className="layout-add-input"
                        autoFocus
                      />
                      <button type="submit" className="layout-add-btn">+ Add</button>
                    </form>
                  </div>
                )}
              </div>
            )}
            <div className="add-panel-container">
              <button
                className="add-panel-btn"
                onClick={() => { setShowAddMenu(!showAddMenu); setShowLayoutMenu(false); }}
                title="Add Panel"
              >
                + Panels
              </button>
              {showAddMenu && (
                <div className="add-panel-menu" onMouseLeave={() => setFlyoutGroup(null)}>
                  {hiddenPanels.length === 0 ? (
                    <span className="add-panel-empty">All panels visible</span>
                  ) : (
                    (() => {
                      const hiddenIds = new Set(hiddenPanels.map((p) => p.id));
                      const groups = Object.entries(PANEL_GROUPS)
                        .map(([group, ids]) => ({
                          group,
                          panels: ids.filter((id) => hiddenIds.has(id)),
                        }))
                        .filter((g) => g.panels.length > 0);
                      return groups.map((g) => (
                        <div
                          key={g.group}
                          className={`add-panel-group-item${flyoutGroup === g.group ? ' active' : ''}`}
                          onMouseEnter={() => setFlyoutGroup(g.group)}
                          onClick={() => setFlyoutGroup(flyoutGroup === g.group ? null : g.group)}
                        >
                          <span className="add-panel-group-label">{g.group}</span>
                          <span className="add-panel-group-arrow">&#x25C2;</span>
                          {flyoutGroup === g.group && (
                            <div className="add-panel-flyout">
                              {g.panels.map((id) => (
                                <button
                                  key={id}
                                  className="add-panel-option"
                                  onClick={(e) => { e.stopPropagation(); restorePanel(id); }}
                                >
                                  {PANEL_NAMES[id] || id}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ));
                    })()
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      <main className="dashboard">
        {layoutConflict && (
          <div style={{ background: '#7c3aed', color: '#fff', padding: '10px 16px', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
            <span>A newer version of your layouts was detected from another device. The page has been refreshed with the latest version.</span>
            <button onClick={() => setLayoutConflict(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, marginLeft: 12 }}>&times;</button>
          </div>
        )}
        {isMobile && layoutIndex === 0 && editMode && (
          <div style={{ background: '#b45309', color: '#fff', padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
            Mobile devices cannot edit the Default layout. Switch to another layout to customize.
          </div>
        )}
        {navHistory.length > 0 && (
          <button className="nav-back-btn" onClick={navigateBack}>
            &lsaquo; Back
          </button>
        )}
        <DashboardPanels
          panelMap={panelMap}
          layout={currentLayout}
          layouts={layouts}
          onLayoutChange={handleLayoutChange}
          editMode={editMode}
          onNavigate={navigateToLayout}
        />
      </main>
      <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      {showLogoutConfirm && (
        <div className="modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="modal-content logout-confirm" onClick={(e) => e.stopPropagation()}>
            <h2>Log Out?</h2>
            <p className="logout-confirm-text">Are you sure you want to log out?</p>
            <div className="logout-confirm-actions">
              <button className="logout-confirm-cancel" onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </button>
              <button className="logout-confirm-yes" onClick={handleLogout}>
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
      {showVersionBanner && (
        <div className="version-banner">
          <div className="version-banner-content">
            <span className="version-banner-icon">&#x1F680;</span>
            <div>
              <div className="version-banner-title">New Version Detected!</div>
              <div className="version-banner-sub">Refreshing in 10 seconds...</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
